import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './LocalBYOC.css';

interface BYOCBin {
  bin_id: number;
  name: string;
  qr_code: string | null;
  product_name: string | null;
  price_per_lb: string | null;
  percentage_full: number;
  current_lbs: string;
  capacity_lbs: string;
  status: 'in_stock' | 'low' | 'very_low';
}

type AnimalFilter = 'all' | 'poultry' | 'goats' | 'horses' | 'cattle' | 'pigs' | 'general';

interface FilterOption {
  key: AnimalFilter;
  label: string;
  keywords: string[];
}

const ANIMAL_FILTERS: FilterOption[] = [
  { key: 'all', label: 'All', keywords: [] },
  { key: 'poultry', label: 'Poultry', keywords: ['flock', 'layer', 'chick', 'poultry', 'chicken', 'hen', 'rooster', 'broiler'] },
  { key: 'goats', label: 'Goats', keywords: ['goat', 'dairy goat', 'caprine'] },
  { key: 'horses', label: 'Horses', keywords: ['horse', 'cavalry', 'equine', 'mare', 'foal'] },
  { key: 'cattle', label: 'Cattle', keywords: ['cattle', 'beef', 'cow', 'calf', 'bovine', 'steer'] },
  { key: 'pigs', label: 'Pigs', keywords: ['pig', 'hog', 'swine', 'porcine', 'sow'] },
  { key: 'general', label: 'General', keywords: ['all stock', 'general', 'multi-species', 'all-purpose'] },
];

const LocalBYOC: React.FC = () => {
  const { hasLocalAccess } = useLocalAccess();
  const [bins, setBins] = useState<BYOCBin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<AnimalFilter>('all');

  useEffect(() => {
    const fetchBYOCStock = async () => {
      try {
        const data = await bridgeClubAPI.getBYOCStock();
        setBins(data);
      } catch (err: any) {
        console.error('Error fetching BYOC stock:', err);
        setError(err.response?.data?.error || 'Failed to load BYOC stock levels');
      } finally {
        setLoading(false);
      }
    };

    if (hasLocalAccess) {
      fetchBYOCStock();
    } else {
      setLoading(false);
    }
  }, [hasLocalAccess]);

  const getStatusColor = (status: string, percentage: number): string => {
    if (percentage > 75) return '#2d5a27'; // Green
    if (percentage > 50) return '#6b8e23'; // Yellow-green
    if (percentage > 25) return '#f5a623'; // Orange
    return '#dc3545'; // Red
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'in_stock': return 'In Stock';
      case 'low': return 'Running Low';
      case 'very_low': return 'Very Low';
      default: return status;
    }
  };

  // Filter bins based on selected animal type
  const filteredBins = useMemo(() => {
    if (activeFilter === 'all') {
      return bins;
    }

    const filterOption = ANIMAL_FILTERS.find(f => f.key === activeFilter);
    if (!filterOption) return bins;

    return bins.filter(bin => {
      const searchText = `${bin.name} ${bin.product_name || ''}`.toLowerCase();
      return filterOption.keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
    });
  }, [bins, activeFilter]);

  // Count bins per filter for showing badges
  const filterCounts = useMemo(() => {
    const counts: Record<AnimalFilter, number> = {
      all: bins.length,
      poultry: 0,
      goats: 0,
      horses: 0,
      cattle: 0,
      pigs: 0,
      general: 0,
    };

    bins.forEach(bin => {
      const searchText = `${bin.name} ${bin.product_name || ''}`.toLowerCase();
      ANIMAL_FILTERS.forEach(filter => {
        if (filter.key !== 'all' && filter.keywords.some(kw => searchText.includes(kw.toLowerCase()))) {
          counts[filter.key]++;
        }
      });
    });

    return counts;
  }, [bins]);

  if (!hasLocalAccess) {
    return (
      <BridgeClubLayout>
        <div className="local-byoc">
          <div className="access-denied">
            <h2>Local Access Required</h2>
            <p>BYOC (Bring Your Own Container) is available for Mill Members only.</p>
            <Link to="/bridge-club/dashboard" className="back-link">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="local-byoc">
        <div className="byoc-header">
          <Link to="/bridge-club/dashboard" className="back-link">
            &larr; Dashboard
          </Link>
          <h1>BYOC Stock Levels</h1>
          <p className="byoc-subtitle">
            Bring Your Own Container - Check current stock before your visit
          </p>
        </div>

        {/* Animal Type Filters */}
        {!loading && bins.length > 0 && (
          <div className="byoc-filters">
            <div className="filter-scroll">
              {ANIMAL_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  className={`filter-pill ${activeFilter === filter.key ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter.key)}
                  disabled={filter.key !== 'all' && filterCounts[filter.key] === 0}
                >
                  {filter.label}
                  {filterCounts[filter.key] > 0 && (
                    <span className="filter-count">{filterCounts[filter.key]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="byoc-loading">
            <div className="spinner"></div>
            <p>Loading stock levels...</p>
          </div>
        ) : error ? (
          <div className="byoc-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        ) : bins.length === 0 ? (
          <div className="byoc-empty">
            <p>No BYOC bins available at this time.</p>
          </div>
        ) : filteredBins.length === 0 ? (
          <div className="byoc-empty">
            <p>No bins match the selected filter.</p>
            <button className="reset-filter-btn" onClick={() => setActiveFilter('all')}>
              Show All Bins
            </button>
          </div>
        ) : (
          <div className="byoc-bins">
            {filteredBins.map((bin) => (
              <div key={bin.bin_id} className="byoc-bin-card">
                <div className="bin-header">
                  <span className="bin-name-tag">{bin.name}</span>
                  <span
                    className={`bin-status status-${bin.status}`}
                    style={{ backgroundColor: getStatusColor(bin.status, bin.percentage_full) }}
                  >
                    {getStatusLabel(bin.status)}
                  </span>
                </div>
                {bin.product_name && (
                  <h3 className="bin-product-name">{bin.product_name}</h3>
                )}
                {bin.price_per_lb && (
                  <p className="bin-price">${parseFloat(bin.price_per_lb).toFixed(2)}/lb</p>
                )}
                <div className="bin-level">
                  <div className="level-bar">
                    <div
                      className="level-fill"
                      style={{
                        width: `${bin.percentage_full}%`,
                        backgroundColor: getStatusColor(bin.status, bin.percentage_full)
                      }}
                    />
                  </div>
                  <span className="level-percent">{bin.percentage_full}% full</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="byoc-info">
          <h3>How BYOC Works</h3>
          <ul>
            <li>Bring a clean container to the mill</li>
            <li>Fill your container from our bulk bins</li>
            <li>Pay by weight at the register</li>
            <li>Fresh feed at wholesale prices!</li>
          </ul>
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default LocalBYOC;
